import { db } from '@/db/client';
import { getSupabase } from '@/lib/supabase';
import type { GameDefinition } from '@/schemas';
import type { UserId } from '@/schemas/ids';
import { encodeGameDefinitionToRow } from '../codec';
import { getUserSyncState, setUserLastUploadedAt } from '../state';

export interface UploadStats {
  uploaded: number;
}

export async function uploadPendingGameDefinitions(userId: UserId): Promise<UploadStats> {
  const client = getSupabase();
  const {
    data: { session },
  } = await client.auth.getSession();
  if (!session?.user) {
    throw new Error('User must be authenticated to sync game definitions');
  }

  const { lastUploadedAt = 0 } = await getUserSyncState(userId, 'game_definitions');
  // Get all game definitions that were created/updated after lastUploadedAt
  // Only sync game definitions with a userId (unauthenticated ones stay local)
  const pending: GameDefinition[] = await db.gameDefinitions
    .filter((game) => {
      // Only sync game definitions that belong to this user
      if (game.userId !== userId) {
        return false;
      }
      if (game.deletedAt) {
        // Include deleted games if they were deleted after lastUploadedAt
        return game.deletedAt > lastUploadedAt;
      }
      // Include non-deleted games if they were created or updated after lastUploadedAt
      return game.createdAt > lastUploadedAt || game.updatedAt > lastUploadedAt;
    })
    .toArray();

  if (pending.length === 0) return { uploaded: 0 };

  const rows = pending.map((game) => ({
    ...encodeGameDefinitionToRow(game),
    user_id: userId,
  }));
  const { error } = await client.from('game_definitions').upsert(rows, { onConflict: 'id' });
  if (error) throw error;

  // Update lastUploadedAt to the max of created_at, updated_at, deleted_at
  const maxTs = Math.max(
    ...pending.map((g) => Math.max(g.createdAt, g.updatedAt, g.deletedAt ?? 0)),
    0,
  );
  if (maxTs > lastUploadedAt) {
    await setUserLastUploadedAt(userId, 'game_definitions', maxTs);
  }
  return { uploaded: pending.length };
}
